<template>
  <v-dialog max-width="1100" v-model="dialog" @click:outside="closeModal">
    <v-card>
      <v-card-title class="justify-space-between">
        <p>{{ $t("reports.point-of-sale.details") }}</p>
        <button @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </button>
      </v-card-title>

      <v-card-text>
        <v-row justify="end" class="mt-2 mb-1 mr-1">
          <v-btn
            class="rounded-xxl"
            v-for="(act, index) in actions"
            :key="index"
            :loading="act.loading"
            @click="act.action()"
            :class="index == 0 ? 'button' : 'button btn-right'"
          >
            {{ act.name }}
            <v-icon>
              {{ act.icon }}
            </v-icon>
          </v-btn>
        </v-row>

        <v-form
          ref="form"
          @submit.prevent="filterPosDetail"
          class="search-forms"
        >
          <v-row>
            <v-col cols="12" sm="4" md="3">
              <BaseTextField
                v-model="filter.search"
                :placeholder="$t('reports.search')"
                @input="handleSearch"
                append-icon="mdi-magnify"
                single-line
                hide-details
                clearable
              ></BaseTextField>
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <BaseSelect
                v-model="filter.paymentMethod"
                :label="$t('reports.payment-method.input')"
                :placeholder="$t('reports.payment-method.select')"
                :items="paymentMethods"
                item-text="key"
                item-value="value"
                clearable
                @click:clear="filter.paymentMethod = ''"
              />
            </v-col>
            <v-col cols="12" sm="4" md="3" v-if="!idProduto">
              <BaseAutocomplete
                v-if="!idProduto"
                v-model="filter.event"
                :label="$t('reports.sales-by-event.event')"
                :placeholder="$t('reports.sales-by-event.select')"
                :no-data-text="$t('reports.sales-by-event.no-events-found')"
                :items="allEvents"
                :menu-props="{ maxHeight: 250 }"
                item-text="descricao"
                item-value="idproduto"
                clearable
                @click:clear="filter.event = ''"
              />
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <date-time-picker
                :label="$t('reports.initial-date')"
                @formatted-date="handleInitDateTime"
              ></date-time-picker>
            </v-col>

            <v-col cols="12" sm="4" md="3">
              <date-time-picker
                :label="$t('reports.final-date')"
                @formatted-date="handleEndDateTime"
              ></date-time-picker>
            </v-col>

            <v-col class="d-flex justify-end">
              <BaseButton
                type="submit"
                :loading="salesByPosDetailLoading"
                variant="filter"
                class="button rounded-xxl"
              >
                {{ $t("reports.filter") }}
              </BaseButton>
            </v-col>
          </v-row>
        </v-form>

        <v-data-table
          :headers="headers"
          :loading="salesByPosDetailLoading"
          :items="tableData"
          :no-data-text="$t('reports.no-info-found')"
          :loading-text="$t('reports.loading-info')"
          :items-per-page="10"
          mobile-breakpoint="0"
          :header-props="{
            class: 'rounded-t-lg',
            color: $config.dados.corinputs,
          }"
          :footer-props="{
            class: 'rounded-b-lg',
            itemsPerPageOptions: [10, 20, 50],
            itemsPerPageText: $t('reports.sales-per-page'),
          }"
        >
          <template v-slot:item.valor="{ item }">
            {{ formatCurrency(item.valor) }}
          </template>

          <template
            v-for="(totais, index) in allSalesByPosDetail.totals"
            v-slot:body.append
          >
            <tr :key="index" class="text-end font-weight-bold total-row">
              <td class="text-start">Total</td>
              <td colspan="2"></td>
              <td>{{ totais.qtd_cortesias }}</td>
              <td>{{ totais.qtd_total }}</td>
              <td>{{ formatCurrency(totais.valor) }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { formatCurrency } from "@/utils";
import _ from "lodash";
import dayjs from "dayjs";

import BaseSelect from "@/components/forms/BaseSelect.vue";
import BaseTextField from "@/components/forms/BaseTextField.vue";
import BaseAutocomplete from "@/components/forms/BaseAutocomplete.vue";
import BaseButton from "@/components/forms/BaseButton.vue";
import DateTimePicker from "@/components/forms/DateTimePicker.vue";
export default {
  components: {
    BaseAutocomplete,
    BaseButton,
    BaseSelect,
    BaseTextField,
    DateTimePicker,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    equipment: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      dayjs: dayjs,
      formatCurrency,
      loadingExportDetailsCSV: false,
      loadingExportDetailsPDF: false,
      filter: {
        search: "",
        initDateTime: "",
        endDateTime: "",
        event: "",
        paymentMethod: "",
      },
    };
  },
  computed: {
    ...mapGetters([
      "allEvents",
      "allSalesByPosDetail",
      "idProduto",
      "paymentMethods",
      "salesByPosDetailLoading",
      "storeSequencial",
    ]),
    actions() {
      return [
        {
          id: "CSV",
          name: "",
          icon: "mdi-microsoft-excel",
          loading: this.loadingExportDetailsCSV,
          action: () => this.exportDetailsCSV(),
        },
        {
          id: "PDF",
          name: "",
          icon: "mdi-file-pdf-box",
          loading: this.loadingExportDetailsPDF,
          action: () => this.exportDetailsPDF(),
        },
      ];
    },
    headers() {
      return [
        {
          text: this.$t("reports.ticket"),
          value: "midia",
          align: "start",
          sortable: true,
          width: 180,
        },
        {
          text: this.$t("reports.point-of-sale.description"),
          value: "descricao",
          align: "start",
          sortable: true,
          width: 180,
        },
        {
          text: this.$t("reports.payment-method.input"),
          value: "metodo_pagamento",
          align: "start",
          sortable: true,
          width: 160,
        },
        {
          text: this.$t("reports.point-of-sale.courtesies"),
          value: "qtd_cortesias",
          align: "end",
          sortable: true,
          width: 100,
        },
        {
          text: this.$t("reports.quantity"),
          value: "qtd_total",
          align: "end",
          sortable: true,
          width: 100,
        },
        {
          text: this.$t("reports.value"),
          value: "valor",
          align: "end",
          sortable: true,
          width: 140,
        },
      ];
    },
    tableData() {
      let info = this.allSalesByPosDetail.info.map(item => {
        return item.length == 0 ? {} : item;
      });

      return info;
    },
    filters() {
      let filter = {};

      if (this.filter.search) {
        Object.assign(filter, {
          pesquisar: this.filter.search,
        });
      }

      if (this.filter.paymentMethod) {
        Object.assign(filter, {
          forma_pagamento: this.filter.paymentMethod,
        });
      }

      if (this.filter.event) {
        Object.assign(filter, {
          idproduto: this.filter.event,
        });
      }

      if (this.filter.initDateTime) {
        Object.assign(filter, {
          dthr_ped_ini: this.filter.initDateTime,
        });
      }

      if (this.filter.endDateTime) {
        Object.assign(filter, {
          dthr_ped_fim: this.filter.endDateTime,
        });
      }

      if (this.idProduto) {
        Object.assign(filter, {
          idproduto: this.idProduto,
        });
      }

      Object.assign(filter, {
        producerId: this.storeSequencial,
        idequipamento: this.equipment,
        getItems: true,
      });

      return filter;
    },
  },
  methods: {
    ...mapActions([
      "exportSalesByPOSDetailCSV",
      "exportSalesByPOSDetailPDF",
      "fetchSalesByPosDetail",
    ]),
    ...mapMutations(["setSalesByPosDetail"]),
    filterPosDetail() {
      this.fetchSalesByPosDetail(this.filters);
    },
    handleSearch: _.debounce(function (event) {
      this.filter.search = event;
      this.filterPosDetail();
    }, 500),
    handleInitDateTime(formatteddatetime) {
      this.filter.initDateTime = formatteddatetime;
    },
    handleEndDateTime(formatteddatetime) {
      this.filter.endDateTime = formatteddatetime;
    },
    closeModal() {
      this.$emit("close");
      this.setSalesByPosDetail({ info: [], totals: [] });
      this.filter = {
        search: "",
        paymentMethod: "",
        event: "",
        initDateTime: "",
        endDateTime: "",
      };
    },
    async exportDetailsCSV() {
      this.loadingExportDetailsCSV = true;
      await this.exportSalesByPOSDetailCSV(this.filters);
      this.loadingExportDetailsCSV = false;
    },
    async exportDetailsPDF() {
      this.loadingExportDetailsPDF = true;
      await this.exportSalesByPOSDetailPDF(this.filters);
      this.loadingExportDetailsPDF = false;
    },
  },
  watch: {
    dialog() {
      if (this.dialog) {
        this.filterPosDetail();
      }
    },
  },
  destroyed() {
    this.filter = {
      search: "",
      paymentMethod: "",
      event: "",
      initDateTime: "",
      endDateTime: "",
    };
  },
};
</script>
